import React, { useEffect, useState } from "react";
import styles from "./MarketplacesV2.module.scss";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { Search } from "lucide-react";
import { lowercaseCompare } from "@src/utils/general.utils";
import { MyTooltip } from "../tooltip/Tooltip";
import { systemActions } from "@src/store";
import { Loader } from "../loader/Loader";
import { buildMarketplaceUnits, isMarketplaceBlockedByTMConsent } from "../utils/marketplaceUtils";
import { MarketplaceIcon } from "./MarketplaceIcon";
import { ExportDestination } from "@src/constants/constants";

type TMarketplacesV2 = {
	selectedExportDestinations: Array<number>;
	onMarkteplaceClick: (x: number) => void;
	compactView?: boolean;
	containerClassName?: string;
	inRestrictedCategory?: boolean;
};

const MarketplacesV2 = ({
	selectedExportDestinations,
	compactView,
	containerClassName,
	onMarkteplaceClick,
	inRestrictedCategory = false,
}: TMarketplacesV2) => {
	const dispatch = useAppDispatch();

	const { data: marketplaces, loading } = useAppSelector((state) => state.system.exportDestinations);
	const hasCIMSMarketAccess = useAppSelector((state) => state.settings.hasCIMSMarketAccess);
	const [searchTerm, setSearchTerm] = useState("");

	const marketplaceUnits = buildMarketplaceUnits(
		marketplaces.filter((x) => hasCIMSMarketAccess || x.Export_Destination_ID !== ExportDestination.Cims),
	);

	const mappedMarketplaces = marketplaceUnits
		.filter((unit) => lowercaseCompare(unit.label, searchTerm))
		.map((unit) => {
			const isDisabled = unit.destinations.some((d) => isMarketplaceBlockedByTMConsent(d, inRestrictedCategory));
			const isSelected = unit.ids.some((id) => selectedExportDestinations.includes(id));
			const notDisabledTooltip = compactView ? unit.label : null;
			const isCompactStacked = compactView && unit.destinations.length > 1;

			return (
				<MyTooltip
					key={unit.key}
					id={`item_${unit.id}`}
					popup={isDisabled ? `No consent to share ${unit.label}` : notDisabledTooltip}
					popupStyle={{ fontSize: "1.2rem", fontWeight: 600 }}
					element={
						<div
							data-disabled={isDisabled}
							data-selected={isSelected}
							onClick={() => {
								if (isDisabled) return;
								onMarkteplaceClick(unit.id);
							}}
							className={
								isCompactStacked ? `${styles.singleMarketplace} ${styles.compactStacked}` : styles.singleMarketplace
							}>
							{unit.destinations.map((d) => (
								<div key={d.Export_Destination_ID} className={styles.imageContainer}>
									<MarketplaceIcon destination={d} />
								</div>
							))}
							{compactView ? null : <div className={styles.marketplaceName}>{unit.label}</div>}
						</div>
					}
				/>
			);
		});

	const skeletonMarketplaces = Array.from({ length: 12 }, (_, i) => (
		<div key={i} className={styles.singleMarketplace} data-disabled="true">
			<div className={styles.imageContainer}>
				<Loader />
			</div>
			<div className={styles.marketplaceName}></div>
		</div>
	));

	useEffect(() => {
		dispatch(systemActions.listExportDestinations({}));
	}, [dispatch]);

	return (
		<>
			<div className={styles.searchContainer}>
				<div className={styles.iconContainer}>
					<Search size={16} />
				</div>
				<input placeholder="Search for vendor" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
			</div>
			<div className={containerClassName ?? styles.marketplacesContainer}>
				{loading ? skeletonMarketplaces : mappedMarketplaces}
			</div>
		</>
	);
};

export default MarketplacesV2;
